import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API } from 'aws-amplify';
import { useTranslation } from 'react-i18next';
import { GET_PUBLIC_CAMPAIGNS } from '../../graphql/queries';
import ChannelIcon from '../campaigns/ChannelIcon';
import CampaignGridCard from '../campaigns/CampaignGridCard';
import { INTEREST_CATEGORIES } from '../../constants/categories';
import { useAuthStore } from '../../store/authStore';
import GlobalNavBar from '../layout/GlobalNavBar';
import {
  saveDiscoveryListState,
  loadDiscoveryListState,
  clearDiscoveryListState,
  type DiscoveryListSnapshot,
} from '../../utils/discoveryListState';
import {
  CATEGORY_LABELS,
  GRADIENT_PALETTES,
  formatDate,
  getDaysRemaining,
  getProgressPercent,
  getChannels,
  getGradient,
  getCategoryLabel,
  getChannelLabel,
  formatRecruitmentPeriod,
  isRecruitmentOpen,
} from '../../utils/campaignUtils';

/* ─── Types ─── */
interface CampaignImage {
  fileKey?: string;
  fileUrl?: string;
  fileName?: string;
  fileSize?: number;
  fileType?: string;
}

interface RecruitmentPeriod {
  startDate?: string;
  endDate?: string;
  alwaysRecruiting?: boolean;
}

interface Campaign {
  campaignId: string;
  campaignName?: string;
  brandName?: string;
  campaignCategory?: string;
  representativeImages?: CampaignImage[];
  contentChannels?: string[];
  contentChannel?: string;
  rewardAmount?: number;
  recruitmentPeriod?: RecruitmentPeriod;
  numberOfRecruits?: number;
  providedValueDescription?: string;
  status?: string;
  campaignType?: string;
  contentTypes?: string[];
  productName?: string;
  recommendedKeywords?: string[];
  createdAt?: string;
  /** Lowercase ISO-3166 alpha-2 (kr / us / jp / …). */
  visitCountry?: string;
  visitCity?: string;
}

/**
 * Emoji flag from a lowercase ISO-3166 alpha-2 code. Returns null for
 * non-2-letter / legacy free-text values so the UI can degrade to a
 * label-only chip instead of rendering broken glyphs.
 */
const countryFlag = (code: string): string | null => {
  if (!/^[a-z]{2}$/.test(code)) return null;
  return String.fromCodePoint(
    ...code.toUpperCase().split('').map((ch) => 0x1f1e6 - 65 + ch.charCodeAt(0)),
  );
};

/* ─── Component-specific constants ─── */
const CHANNELS = ['instagram', 'blog', 'youtube'] as const;
const PAGE_SIZE = 9;

/* ─── Component ─── */
const PublicDashboardV2: React.FC = () => {
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation('common');

  // Read the persisted snapshot once, synchronously, BEFORE any useState
  // initializer runs so that the very first render already reflects the
  // restored values (no flash of default state). The snapshot is consumed
  // by the scroll-restore effect (see below) which calls
  // clearDiscoveryListState after applying the saved scroll position.
  const initialSnapshot = useMemo(() => loadDiscoveryListState(), []);

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSnapshot?.searchQuery ?? '');
  const [selectedCategory, setSelectedCategory] = useState<string>(
    initialSnapshot?.selectedCategory ?? '',
  );
  const [selectedChannels, setSelectedChannels] = useState<Set<string>>(
    new Set(initialSnapshot?.selectedChannels ?? []),
  );
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(
    new Set(initialSnapshot?.selectedCountries ?? []),
  );
  const [recruitStatus, setRecruitStatus] = useState<'all' | 'open' | 'closed'>(
    initialSnapshot?.recruitStatus ?? 'all',
  );
  const [rewardFilter, setRewardFilter] = useState<'all' | 'with' | 'without'>(
    initialSnapshot?.rewardFilter ?? 'all',
  );
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(
    initialSnapshot?.viewMode ?? 'grid',
  );
  const [currentPage, setCurrentPage] = useState(initialSnapshot?.currentPage ?? 1);
  // Mobile filter drawer open state. Not persisted — always starts closed.
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const fetchedRef = React.useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    const fetchCampaigns = async () => {
      try {
        const response = await API.graphql({
          query: GET_PUBLIC_CAMPAIGNS,
          authMode: 'AWS_IAM',
        });
        const fetched = (response as any).data?.getPublicCampaigns?.campaigns || [];
        setCampaigns(fetched);
      } catch (err) {
        console.error('Failed to fetch campaigns:', err);
        setCampaigns([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, []);

  const toggleChannel = (ch: string) => {
    setSelectedChannels((prev) => {
      const next = new Set(prev);
      next.has(ch) ? next.delete(ch) : next.add(ch);
      return next;
    });
  };

  const toggleCountry = (code: string) => {
    setSelectedCountries((prev) => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  // Snapshot the current discovery list state synchronously before the
  // route transition into a campaign detail. The save is wired only on
  // the grid cards and list rows of `/discovery` itself — the recommended
  // widget intentionally does not save (Requirement 1.6).
  const handleNavigateToDetail = useCallback((): void => {
    const snapshot: DiscoveryListSnapshot = {
      searchQuery,
      selectedCategory,
      selectedChannels: Array.from(selectedChannels).sort(),
      selectedCountries: Array.from(selectedCountries).sort(),
      recruitStatus,
      rewardFilter,
      viewMode,
      currentPage,
      scrollY: window.scrollY,
    };
    saveDiscoveryListState(snapshot);
  }, [
    searchQuery,
    selectedCategory,
    selectedChannels,
    selectedCountries,
    recruitStatus,
    rewardFilter,
    viewMode,
    currentPage,
  ]);

  // Available countries with campaign counts, derived from the full
  // campaign pool (not the filtered subset) so the rail always shows the
  // complete region list. Counts reflect the OTHER active filters
  // (category / search / channel / status / reward) but deliberately
  // ignore the country selection itself — otherwise selecting one
  // country would zero out the counts of all the others. Sorted by count
  // desc so the busiest regions sit at the top, matching the redesign.
  const countryOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of campaigns) {
      const code = (c.visitCountry || '').toLowerCase();
      if (!code) continue;
      // Apply the same non-country predicates used in `filtered` so the
      // count is honest about what clicking the country would surface.
      if (selectedCategory && c.campaignCategory !== selectedCategory) continue;
      const q = searchQuery.toLowerCase();
      if (q) {
        const match =
          (c.campaignName || '').toLowerCase().includes(q) ||
          (c.brandName || '').toLowerCase().includes(q) ||
          (c.providedValueDescription || '').toLowerCase().includes(q);
        if (!match) continue;
      }
      if (selectedChannels.size > 0) {
        const chs = getChannels(c);
        if (!chs.some((ch) => selectedChannels.has(ch))) continue;
      }
      if (recruitStatus !== 'all') {
        const days = c.recruitmentPeriod?.alwaysRecruiting
          ? 999
          : getDaysRemaining(c.recruitmentPeriod?.endDate);
        if (recruitStatus === 'open' && days !== null && days < 0) continue;
        if (recruitStatus === 'closed' && (days === null || days >= 0)) continue;
      }
      if (rewardFilter !== 'all') {
        const hasReward = typeof c.rewardAmount === 'number' && c.rewardAmount > 0;
        if (rewardFilter === 'with' && !hasReward) continue;
        if (rewardFilter === 'without' && hasReward) continue;
      }
      counts.set(code, (counts.get(code) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count || a.code.localeCompare(b.code));
  }, [campaigns, selectedCategory, searchQuery, selectedChannels, recruitStatus, rewardFilter]);

  const filtered = useMemo(() => {
    const result = campaigns.filter((c) => {
      // category
      if (selectedCategory && c.campaignCategory !== selectedCategory) return false;
      // country (multi-select; empty = all)
      if (selectedCountries.size > 0) {
        const code = (c.visitCountry || '').toLowerCase();
        if (!code || !selectedCountries.has(code)) return false;
      }
      // search
      const q = searchQuery.toLowerCase();
      if (q) {
        const match =
          (c.campaignName || '').toLowerCase().includes(q) ||
          (c.brandName || '').toLowerCase().includes(q) ||
          (c.providedValueDescription || '').toLowerCase().includes(q);
        if (!match) return false;
      }
      // channel filter
      if (selectedChannels.size > 0) {
        const chs = getChannels(c);
        const hasMatch = chs.some((ch) => selectedChannels.has(ch));
        if (!hasMatch) return false;
      }
      // recruit status
      if (recruitStatus !== 'all') {
        const days = c.recruitmentPeriod?.alwaysRecruiting ? 999 : getDaysRemaining(c.recruitmentPeriod?.endDate);
        if (recruitStatus === 'open' && days !== null && days < 0) return false;
        if (recruitStatus === 'closed' && (days === null || days >= 0)) return false;
      }
      // reward filter
      if (rewardFilter !== 'all') {
        const hasReward = typeof c.rewardAmount === 'number' && c.rewardAmount > 0;
        if (rewardFilter === 'with' && !hasReward) return false;
        if (rewardFilter === 'without' && hasReward) return false;
      }
      return true;
    });

    // Sort: expired campaigns to bottom, rest by newest first
    const now = new Date();
    const isExpired = (c: any) => {
      if (c.recruitmentPeriod?.alwaysRecruiting) return false;
      if (!c.recruitmentPeriod?.endDate) return false;
      return new Date(c.recruitmentPeriod.endDate) < now;
    };
    result.sort((a, b) => {
      const aExp = isExpired(a) ? 1 : 0;
      const bExp = isExpired(b) ? 1 : 0;
      if (aExp !== bExp) return aExp - bExp;
      return (b.createdAt || '').localeCompare(a.createdAt || '');
    });

    return result;
  }, [campaigns, selectedCategory, searchQuery, selectedChannels, selectedCountries, recruitStatus, rewardFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paginated = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const recommended = campaigns.slice(0, 3);

  // Reset to page 1 whenever the user actively changes a filter, the
  // search query, or the channel set. The first run is suppressed so a
  // restored `currentPage` from `initialSnapshot` survives the initial
  // mount. The same first-run branch also clamps a restored page that
  // exceeds the post-load `totalPages` (e.g. the underlying campaigns
  // pool shrank since the snapshot was taken). The clamp waits until
  // `loading === false` so `totalPages` reflects real data.
  // First-mount clamp + ready flag. Runs once when `loading` flips to
  // false; the ref guard makes subsequent re-renders inert. We split
  // this from the filter-reset effect so each effect's dependency array
  // is honest — no `eslint-disable-next-line react-hooks/exhaustive-deps`
  // and no risk of the reset branch firing on the very first render.
  const didMountForFiltersRef = useRef(false);
  useEffect(() => {
    if (didMountForFiltersRef.current) return;
    if (loading) return;
    didMountForFiltersRef.current = true;
    if (initialSnapshot && currentPage > totalPages) {
      setCurrentPage(Math.max(1, totalPages));
    }
  }, [loading, initialSnapshot, currentPage, totalPages]);

  // Reset to page 1 whenever the user actively changes a filter, the
  // search query, or the channel set. Skipped until the first-mount
  // effect above flips the ref, so a restored `currentPage` survives
  // the initial render.
  useEffect(() => {
    if (!didMountForFiltersRef.current) return;
    setCurrentPage(1);
  }, [selectedCategory, searchQuery, selectedChannels, selectedCountries, recruitStatus, rewardFilter]);

  // Scroll to top when navigating between pagination pages. Guarded on a
  // ref so the initial mount (currentPage starts at 1) doesn't force a
  // scroll before the user has a chance to land on a deep link.
  const didMountPageRef = React.useRef(false);
  useEffect(() => {
    if (!didMountPageRef.current) {
      didMountPageRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage]);

  // One-shot scroll restore. When the snapshot captured a `scrollY`,
  // wait until campaigns have loaded and the paginated slice has
  // painted, then jump (no smooth-scroll) to the saved offset on the
  // next animation frame so layout has settled. Once applied, the
  // pending offset is cleared from the ref so subsequent paginations
  // and filter changes don't re-trigger this effect, and the
  // sessionStorage entry is consumed via clearDiscoveryListState() so
  // a fresh visit starts clean.
  const pendingScrollRef = useRef<number | null>(initialSnapshot?.scrollY ?? null);
  useEffect(() => {
    if (pendingScrollRef.current == null) return;
    if (loading) return;
    if (paginated.length === 0) return;
    const target = pendingScrollRef.current;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      window.scrollTo({ top: target, behavior: 'auto' });
      clearDiscoveryListState();
    });
  }, [loading, paginated.length]);

  /* ═══════════════════════════════════════════
     CATEGORY TABS
     ═══════════════════════════════════════════ */
  const renderCategoryTabs = () => (
    <div className="bg-white border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-4">
        <div className="flex gap-1 overflow-x-auto py-2 scrollbar-hide">
          <button
            onClick={() => setSelectedCategory('')}
            className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all ${
              !selectedCategory ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t('publicDashboard.all')}
          </button>
          {INTEREST_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(selectedCategory === cat ? '' : cat)}
              className={`flex-shrink-0 px-4 py-1.5 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                selectedCategory === cat ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              {t(`categories.${cat}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     FILTER CONTENT (shared by desktop rail + mobile drawer)
     ═══════════════════════════════════════════ */
  const activeFilterCount =
    selectedCountries.size +
    selectedChannels.size +
    (recruitStatus !== 'all' ? 1 : 0) +
    (rewardFilter !== 'all' ? 1 : 0);

  const resetAllFilters = () => {
    setSelectedCountries(new Set());
    setSelectedChannels(new Set());
    setRecruitStatus('all');
    setRewardFilter('all');
  };

  // The filter groups, shared verbatim between the sticky desktop rail
  // and the mobile bottom-drawer. Kept as a render helper (not a child
  // component) so it closes over the page's filter state without prop
  // drilling. `idPrefix` keeps element keys unique when both the rail and
  // the drawer are mounted in the DOM at the same time.
  const renderFilterGroups = (idPrefix: string) => (
    <>
      {/* 지역 (region / country) */}
      {countryOptions.length > 0 && (
        <div className="px-4 py-4 border-b border-gray-50">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">
            {t('publicDashboard.region')}
          </div>
          <div className="flex flex-col gap-0.5">
            {/* 전체 — clears the country selection */}
            <button
              onClick={() => setSelectedCountries(new Set())}
              className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-colors ${
                selectedCountries.size === 0 ? 'bg-blue-50' : 'hover:bg-gray-50'
              }`}
            >
              <span
                className={`w-[17px] h-[17px] rounded-[5px] border flex-shrink-0 flex items-center justify-center transition-colors ${
                  selectedCountries.size === 0
                    ? 'bg-blue-600 border-blue-600'
                    : 'border-gray-300'
                }`}
              >
                {selectedCountries.size === 0 && (
                  <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 10" fill="none">
                    <path d="M1 5l3.5 3.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                )}
              </span>
              <span className="text-[13.5px] font-semibold text-gray-900">{t('publicDashboard.all')}</span>
              <span className="ml-auto text-[11px] text-gray-300 font-mono">{filtered.length}</span>
            </button>

            {countryOptions.map(({ code, count }) => {
              const on = selectedCountries.has(code);
              const flag = countryFlag(code);
              return (
                <button
                  key={`${idPrefix}-${code}`}
                  onClick={() => toggleCountry(code)}
                  className={`flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-colors ${
                    on ? 'bg-blue-50' : 'hover:bg-gray-50'
                  }`}
                >
                  <span
                    className={`w-[17px] h-[17px] rounded-[5px] border flex-shrink-0 flex items-center justify-center transition-colors ${
                      on ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                    }`}
                  >
                    {on && (
                      <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 12 10" fill="none">
                        <path d="M1 5l3.5 3.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  {flag && <span className="text-sm leading-none" aria-hidden="true">{flag}</span>}
                  <span className="text-[13.5px] font-semibold text-gray-900 truncate">
                    {t(`countries.${code}`, { defaultValue: code.toUpperCase() })}
                  </span>
                  <span className="ml-auto text-[11px] text-gray-300 font-mono">{count}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 채널 유형 */}
      <div className="px-4 py-4 border-b border-gray-50">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">
          {t('publicDashboard.channelType')}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {CHANNELS.map((ch) => {
            const on = selectedChannels.has(ch);
            return (
              <button
                key={`${idPrefix}-${ch}`}
                onClick={() => toggleChannel(ch)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-colors ${
                  on
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-900'
                }`}
              >
                <ChannelIcon channel={ch} className="w-3.5 h-3.5" />
                <span className="capitalize">{ch}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* 모집 상태 */}
      <div className="px-4 py-4 border-b border-gray-50">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">
          {t('publicDashboard.recruitStatus')}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {([
            ['all', t('publicDashboard.recruitAll')],
            ['open', t('publicDashboard.recruitOpen')],
            ['closed', t('publicDashboard.recruitClosed')],
          ] as [typeof recruitStatus, string][]).map(([val, label]) => {
            const on = recruitStatus === val;
            return (
              <button
                key={`${idPrefix}-${val}`}
                onClick={() => setRecruitStatus(val)}
                className={`px-2.5 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-colors ${
                  on
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* 리워드 */}
      <div className="px-4 py-4">
        <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2.5">
          {t('publicDashboard.rewardFilter')}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {([
            ['all', t('publicDashboard.rewardAll')],
            ['with', t('publicDashboard.rewardWith')],
            ['without', t('publicDashboard.rewardWithout')],
          ] as [typeof rewardFilter, string][]).map(([val, label]) => {
            const on = rewardFilter === val;
            return (
              <button
                key={`${idPrefix}-${val}`}
                onClick={() => setRewardFilter(val)}
                className={`px-2.5 py-1.5 rounded-lg text-[12.5px] font-semibold border transition-colors ${
                  on
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400 hover:text-gray-900'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════════
     LEFT SIDEBAR FILTERS (desktop)
     ═══════════════════════════════════════════ */
  const renderSidebar = () => (
    <aside className="w-60 flex-shrink-0 hidden lg:block">
      <div className="sticky top-[7.5rem] bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        {/* Rail header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100">
          <span className="text-sm font-bold text-gray-900">{t('publicDashboard.filters')}</span>
          {activeFilterCount > 0 && (
            <button
              onClick={resetAllFilters}
              className="text-[11px] font-semibold text-blue-600 hover:underline"
            >
              {t('publicDashboard.reset')}
            </button>
          )}
        </div>
        {renderFilterGroups('rail')}
      </div>
    </aside>
  );

  /* ═══════════════════════════════════════════
     MOBILE FILTER DRAWER (slide-in from right)
     ═══════════════════════════════════════════ */
  const renderMobileFilterDrawer = () => (
    <>
      {/* Backdrop */}
      <div
        onClick={() => setMobileFilterOpen(false)}
        className={`lg:hidden fixed inset-0 z-[290] bg-black/40 transition-opacity duration-200 ${
          mobileFilterOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        aria-hidden="true"
      />
      {/* Drawer panel */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('publicDashboard.filters')}
        className={`lg:hidden fixed top-0 right-0 bottom-0 z-[300] w-[min(340px,86vw)] bg-white shadow-2xl flex flex-col transition-transform duration-250 ${
          mobileFilterOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Drawer header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100 flex-shrink-0">
          <span className="text-base font-bold text-gray-900">{t('publicDashboard.filters')}</span>
          <div className="flex items-center gap-3">
            {activeFilterCount > 0 && (
              <button
                onClick={resetAllFilters}
                className="text-[12px] font-semibold text-blue-600 hover:underline"
              >
                {t('publicDashboard.reset')}
              </button>
            )}
            <button
              onClick={() => setMobileFilterOpen(false)}
              className="w-8 h-8 -mr-1.5 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400"
              aria-label={t('publicDashboard.close', { defaultValue: '닫기' })}
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable filter groups */}
        <div className="flex-1 overflow-y-auto">{renderFilterGroups('drawer')}</div>

        {/* Sticky footer — apply / view results */}
        <div className="flex-shrink-0 border-t border-gray-100 p-4">
          <button
            onClick={() => setMobileFilterOpen(false)}
            className="w-full py-3 rounded-xl bg-gray-900 text-white text-sm font-bold hover:bg-blue-600 transition-colors"
          >
            {t('publicDashboard.showResults', { count: filtered.length, defaultValue: '캠페인 보기' })}
          </button>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════════
     VIEW TOGGLE + GRID/LIST ICONS
     ═══════════════════════════════════════════ */
  const renderViewToggle = () => (
    <div className="flex items-center justify-between mb-4">
      <p className="text-sm text-gray-500">
        <span dangerouslySetInnerHTML={{ __html: t('publicDashboard.totalCampaigns', { count: filtered.length }) }} />
      </p>
      <div className="flex items-center gap-2">
        {/* Mobile filter trigger — hidden on lg+ where the rail is visible */}
        <button
          onClick={() => setMobileFilterOpen(true)}
          className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-[13px] font-semibold text-gray-900 hover:border-gray-400 transition-colors"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 5h18M6 12h12M10 19h4" strokeLinecap="round" />
          </svg>
          {t('publicDashboard.filters')}
          {activeFilterCount > 0 && (
            <span className="ml-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-blue-600 text-white text-[10px] font-mono font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
          <button
            onClick={() => setViewMode('grid')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            aria-label={t('publicDashboard.gridView')}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="1" y="1" width="6" height="6" rx="1" />
              <rect x="9" y="1" width="6" height="6" rx="1" />
              <rect x="1" y="9" width="6" height="6" rx="1" />
              <rect x="9" y="9" width="6" height="6" rx="1" />
            </svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-400 hover:text-gray-600'}`}
            aria-label={t('publicDashboard.listView')}
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 16 16">
              <rect x="1" y="1" width="14" height="3" rx="1" />
              <rect x="1" y="6" width="14" height="3" rx="1" />
              <rect x="1" y="11" width="14" height="3" rx="1" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════════
     LIST ROW
     ═══════════════════════════════════════════ */
  const renderListRow = (c: Campaign, idx: number) => {
    const img = c.representativeImages?.[0]?.fileUrl;
    const channels = getChannels(c);
    const days = c.recruitmentPeriod?.alwaysRecruiting ? null : getDaysRemaining(c.recruitmentPeriod?.endDate);
    const gradient = getGradient(idx);

    return (
      <div key={c.campaignId} className="flex items-center gap-4 bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md hover:border-gray-300 transition-all">
        {/* Small thumbnail */}
        <div className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0">
          {img ? (
            <img src={img} alt={c.campaignName || ''} className="w-full h-full object-cover" loading="lazy" />
          ) : (
            <div className={`w-full h-full bg-gradient-to-br ${gradient} flex items-center justify-center`}>
              <span className="text-white/80 text-lg font-bold">{(c.campaignName || 'C')[0]}</span>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{c.campaignName || t('publicDashboard.campaign')}</h3>
          <p className="text-xs text-gray-400 truncate">{c.brandName || ''}</p>
        </div>

        {/* Channels */}
        <div className="hidden sm:flex items-center gap-1">
          {channels.map((ch, i) => (
            <ChannelIcon key={i} channel={ch} className="w-4 h-4" />
          ))}
        </div>

        {/* Reward — render only when the campaign has a real amount.
            Currency-aware so KR users see "원" rather than a $ prefix on
            won-denominated values. */}
        {typeof c.rewardAmount === 'number' && c.rewardAmount > 0 && (
          <span className="hidden md:inline-block px-2 py-0.5 bg-blue-50 text-blue-700 text-xs font-semibold rounded-md whitespace-nowrap">
            {i18n.language?.startsWith('ko')
              ? `${c.rewardAmount.toLocaleString('ko-KR')}원`
              : `$${c.rewardAmount.toLocaleString('en-US')}`}
          </span>
        )}

        {/* Deadline */}
        <div className="hidden lg:block text-xs text-gray-400 whitespace-nowrap">
          {c.recruitmentPeriod?.alwaysRecruiting ? (
            <span className="text-emerald-500 font-medium">{t('publicDashboard.alwaysRecruiting')}</span>
          ) : days !== null && days >= 0 ? (
            <span className={days < 3 ? 'text-red-500 font-bold' : ''}>D-{days}</span>
          ) : days !== null ? (
            <span className="text-gray-300">{t('publicDashboard.closed')}</span>
          ) : null}
        </div>

        {/* Apply button */}
        <Link
          to={`/campaigns/${c.campaignId}`}
          onClick={handleNavigateToDetail}
          className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
        >
          지원하기
        </Link>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     RECOMMENDED WIDGET (right sticky)
     ═══════════════════════════════════════════ */
  const renderRecommended = () => (
    <aside className="w-64 flex-shrink-0 hidden xl:block">
      <div className="sticky top-[7.5rem]">
        <h3 className="text-sm font-semibold text-gray-900 mb-3">{t('publicDashboard.recommended')}</h3>
        <div className="space-y-3">
          {recommended.map((c, idx) => {
            const img = c.representativeImages?.[0]?.fileUrl;
            const gradient = getGradient(idx);
            return (
              <Link
                key={c.campaignId}
                to={`/campaigns/${c.campaignId}`}
                className="block rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-all"
              >
                <div className="h-20 overflow-hidden">
                  {img ? (
                    <img src={img} alt={c.campaignName || ''} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${gradient}`} />
                  )}
                </div>
                <div className="p-2.5">
                  <p className="text-xs font-semibold text-gray-900 line-clamp-1">{c.campaignName || t('publicDashboard.campaign')}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">{c.brandName || ''}</p>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </aside>
  );

  /* ═══════════════════════════════════════════
     PAGINATION
     ═══════════════════════════════════════════ */
  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-center gap-3 mt-8">
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          이전
        </button>
        <span className="text-sm text-gray-500">
          {currentPage} / {totalPages}
        </span>
        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          다음
        </button>
      </div>
    );
  };

  /* ═══════════════════════════════════════════
     LOADING SKELETON
     ═══════════════════════════════════════════ */
  const renderSkeleton = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="h-40 bg-gray-200 animate-pulse" />
          <div className="p-3.5 space-y-2">
            <div className="h-3 bg-gray-200 rounded animate-pulse w-1/3" />
            <div className="h-4 bg-gray-200 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-1/2" />
            <div className="h-8 bg-gray-100 rounded-lg animate-pulse mt-3" />
          </div>
        </div>
      ))}
    </div>
  );

  /* ═══════════════════════════════════════════
     EMPTY STATE
     ═══════════════════════════════════════════ */
  const renderEmpty = () => (
    <div className="text-center py-16">
      <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <p className="text-gray-400 text-sm">{t('publicDashboard.noResults')}</p>
      <p className="text-gray-300 text-xs mt-1">{t('publicDashboard.noResultsHint')}</p>
    </div>
  );

  /* ═══════════════════════════════════════════
     MAIN RENDER
     ═══════════════════════════════════════════ */
  return (
    <div className="min-h-screen bg-gray-50">
      <GlobalNavBar variant="dark" />
      {renderCategoryTabs()}
      {renderMobileFilterDrawer()}

      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Left sidebar */}
          {renderSidebar()}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {renderViewToggle()}

            {loading ? (
              renderSkeleton()
            ) : paginated.length === 0 ? (
              renderEmpty()
            ) : viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {paginated.map((c, i) => (
                  <CampaignGridCard
                    key={c.campaignId}
                    campaign={c}
                    index={i}
                    onNavigate={handleNavigateToDetail}
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {paginated.map((c, i) => renderListRow(c, i))}
              </div>
            )}

            {renderPagination()}
          </div>

          {/* Right recommended widget */}
          {renderRecommended()}
        </div>
      </div>
    </div>
  );
};

export default PublicDashboardV2;
